/**
 * The Systems page's reads and its two acts (`/api/admin/systems/*`,
 * superadmin only). House style: plain `fetch`, `credentials: "include"`, no
 * shared client, no query cache. The overview polls every 30 seconds while
 * the tab is visible.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  SystemsOutcome,
  SystemsOverview,
  SystemsRunDetail,
  SystemsRunRow,
  SystemsRuntime,
  SystemsSignal,
  SystemsStream,
  SystemsStreamKind,
} from "@yagni/shared";

export const SYSTEMS_POLL_MS = 30_000;
const SIGNALS_POLL_MS = 120_000;

export interface SystemsQuery {
  days: 7 | 14 | 30;
  /** `YYYY-MM-DD`; omitted is today. */
  day?: string | null;
  workspaceId?: string | null;
  deskId?: string | null;
  worker?: string | null;
  outcome?: SystemsOutcome | null;
  runtime?: SystemsRuntime | null;
}

export function systemsSearch(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

async function readJson<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `${what} failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export async function fetchSystemsOverview(query: SystemsQuery): Promise<SystemsOverview> {
  const res = await fetch(`/api/admin/systems/overview${systemsSearch({ ...query })}`, { credentials: "include" });
  return readJson<SystemsOverview>(res, "The Systems read");
}

export async function fetchSystemsSignals(): Promise<SystemsSignal[]> {
  const res = await fetch("/api/admin/systems/signals", { credentials: "include" });
  return readJson<SystemsSignal[]>(res, "The signals read");
}

export async function fetchSystemsRuns(params: {
  day?: string | null;
  days?: number;
  code?: string | null;
  workspaceId?: string | null;
  deskId?: string | null;
  limit?: number;
}): Promise<SystemsRunRow[]> {
  const res = await fetch(`/api/admin/systems/runs${systemsSearch({ limit: 50, ...params })}`, { credentials: "include" });
  return readJson<SystemsRunRow[]>(res, "The runs read");
}

export async function fetchSystemsRun(runId: string): Promise<SystemsRunDetail> {
  const res = await fetch(`/api/admin/systems/runs/${encodeURIComponent(runId)}`, { credentials: "include" });
  return readJson<SystemsRunDetail>(res, "The run read");
}

/** Stop (take over) or retry a run, as the operator. Resolves with the server's sentence on a refusal. */
export async function actOnSystemsRun(runId: string, act: "stop" | "again"): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`/api/admin/systems/runs/${encodeURIComponent(runId)}/${act}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (res.ok) return { ok: true };
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  return { ok: false, error: body?.error ?? `That did not go through (${res.status})` };
}

function useVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === "undefined" || document.visibilityState !== "hidden");
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return visible;
}

export interface Polled<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

/** A read that re-runs every `intervalMs` while the tab is visible, and at once when it comes back. */
function usePolled<T>(read: () => Promise<T>, key: string, intervalMs: number): Polled<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const visible = useVisible();
  const readRef = useRef(read);
  readRef.current = read;
  const latest = useRef(0);

  const refresh = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const next = await readRef.current();
      if (ticket !== latest.current) return;
      setData(next);
      setError(null);
    } catch (err) {
      if (ticket !== latest.current) return;
      setError(err instanceof Error ? err.message : "The read failed");
    } finally {
      if (ticket === latest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [key, refresh]);

  useEffect(() => {
    if (!visible) return;
    const timer = window.setInterval(() => void refresh(), intervalMs);
    return () => window.clearInterval(timer);
  }, [visible, intervalMs, refresh]);

  useEffect(() => {
    if (visible) void refresh();
  }, [visible, refresh]);

  return { data, error, loading, refresh };
}

export const STREAM_POLL_MS = 10_000;

export interface StreamQuery {
  workspaceId: string | null;
  kinds: SystemsStreamKind[];
  internal: boolean;
  before?: string | null;
}

export async function fetchSystemsStream(query: StreamQuery): Promise<SystemsStream> {
  const search = systemsSearch({
    workspaceId: query.workspaceId,
    kinds: query.kinds.join(","),
    internal: query.internal ? 1 : null,
    before: query.before ?? null,
    limit: 100,
  });
  const res = await fetch(`/api/admin/systems/stream${search}`, { credentials: "include" });
  return readJson<SystemsStream>(res, "The Stream read");
}

/** The newest page of the Stream, re-read every ten seconds while the tab is visible. */
export function useSystemsStream(query: Omit<StreamQuery, "before">): Polled<SystemsStream> {
  const key = systemsSearch({
    workspaceId: query.workspaceId,
    kinds: query.kinds.join(","),
    internal: query.internal ? 1 : null,
  });
  return usePolled(() => fetchSystemsStream(query), key, STREAM_POLL_MS);
}

export function useSystemsOverview(query: SystemsQuery): Polled<SystemsOverview> {
  const key = systemsSearch({ ...query });
  return usePolled(() => fetchSystemsOverview(query), key, SYSTEMS_POLL_MS);
}

export function useSystemsSignals(): Polled<SystemsSignal[]> {
  return usePolled(fetchSystemsSignals, "signals", SIGNALS_POLL_MS);
}

/** A one-shot read for a sheet, keyed so a new record reads again. */
export function useOnce<T>(read: () => Promise<T>, key: string): { data: T | null; error: string | null; reload: () => void } {
  const [state, setState] = useState<{ data: T | null; error: string | null }>({ data: null, error: null });
  const [nonce, setNonce] = useState(0);
  const readRef = useRef(read);
  readRef.current = read;
  useEffect(() => {
    let live = true;
    setState({ data: null, error: null });
    readRef
      .current()
      .then((data) => live && setState({ data, error: null }))
      .catch((err: unknown) => live && setState({ data: null, error: err instanceof Error ? err.message : "The read failed" }));
    return () => {
      live = false;
    };
  }, [key, nonce]);
  return { ...state, reload: () => setNonce((n) => n + 1) };
}
